import React, { useState, useCallback } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Chip
} from '@mui/material';
import { useGetUserPushHistoryQuery } from '../../redux/api/pushTemplatesApi';
import moment from 'moment';

const PREFIX = 'UserPushHistoryContent';

const classes = {
  container: `${PREFIX}-container`,
  emptyState: `${PREFIX}-emptyState`,
  loadingState: `${PREFIX}-loadingState`
};

const Root = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    padding: '20px'
  },
  [`& .${classes.emptyState}`]: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  [`& .${classes.loadingState}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px'
  }
}));

const TableHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: 'transparent',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  '&:first-of-type': {
    paddingLeft: '20px'
  },
  '&:last-of-type': {
    paddingRight: '20px'
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease'
}));

interface Props {
  userId: string;
}

const UserPushHistoryContent = ({ userId }: Props) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isLoading, isFetching } = useGetUserPushHistoryQuery({
    userIds: userId,
    page: page + 1,
    limit: rowsPerPage
  });

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const notifications = data?.data?.notifications || [];
  const pagination = data?.pagination;

  if (isLoading) {
    return (
      <Root className={classes.container}>
        <Box className={classes.loadingState}>
          <CircularProgress />
        </Box>
      </Root>
    );
  }

  if (notifications.length === 0) {
    return (
      <Root className={classes.container}>
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6" color="textSecondary">
              Історія push-повідомлень порожня
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Цьому користувачу ще не надсилалися push-повідомлення
            </Typography>
          </Box>
        </Paper>
      </Root>
    );
  }

  return (
    <Root className={classes.container}>
      {isFetching && (
        <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableHeaderRow>
              <TableHeaderCell>Текст повідомлення</TableHeaderCell>
              <TableHeaderCell>Тіло повідомлення</TableHeaderCell>
              <TableHeaderCell>Дата відправлення</TableHeaderCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {notifications.map((notification) => (
              <StyledTableRow key={notification._id}>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontWeight: 600
                    }}
                  >
                    {notification.text}
                  </Typography>
                </TableCell>
                <TableCell>
                  {notification.body ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {notification.body}
                    </Typography>
                  ) : (
                    <Chip 
                      label="Без тіла" 
                      size="small" 
                      sx={{ 
                        borderRadius: '6px',
                        backgroundColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'
                      }} 
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {moment(notification.createdAt).format('DD.MM.YYYY')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {moment(notification.createdAt).format('HH:mm:ss')}
                    </Typography>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.count}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Рядків на сторінці:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} з ${count}`}
          sx={{
            borderTop: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`
          }}
        />
      )}
    </Root>
  );
};

export default UserPushHistoryContent;
